import { useEffect, useRef, useState } from "react";
import { ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useOptimizedProfileData } from "@/hooks/profile/useOptimizedProfileData";
import { useNotifications } from "@/hooks/useNotifications";
import { useUserVerification } from "@/components/profile/hooks/useUserVerification";
import { AccountMenuBody } from "./AccountMenuBody";

export function RailAccountMenu() {
  const { user, userRole, signOut } = useAuth();
  const { profile } = useOptimizedProfileData(user?.id);
  const { unreadCount } = useNotifications();
  const { isVerified } = useUserVerification(user);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user) return null;

  const displayName =
    profile?.display_name || profile?.name || user.email?.split("@")[0] || "User";

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Signed out successfully");
      navigate("/");
    } catch (error) {
      console.error("Sign out error:", error);
      toast.error("Failed to sign out");
    }
  };

  return (
    <div ref={containerRef} className="relative px-3.5">
      {open && (
        <div className="absolute left-3.5 right-3.5 bottom-[calc(100%+8px)] bg-[#17143A] border border-white/[0.13] rounded-[3px] shadow-[0_18px_44px_rgba(0,0,0,0.45)] overflow-hidden">
          <AccountMenuBody
            variant="dark"
            displayName={displayName}
            email={user.email}
            role={userRole}
            isVerified={isVerified}
            unreadNotifications={unreadCount}
            onNavigate={() => setOpen(false)}
            onSignOut={handleSignOut}
          />
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-[11px] w-full box-border p-2.5 bg-white/[0.06] border border-white/10 rounded-[3px] text-left"
      >
        <img
          src={profile?.avatar_url || "/placeholder.svg"}
          alt={displayName}
          className="h-[34px] w-[34px] flex-shrink-0 rounded-full object-cover bg-[#0E0C24]"
        />
        <span className="flex-1 min-w-0 block">
          <span className="block font-semibold text-[13px] leading-tight text-white truncate">
            {displayName}
          </span>
          <span className="block mt-[3px] text-[11px] leading-tight text-[#9A96C4] capitalize">
            {userRole || "Guest"}
            {!isVerified && " · Free plan"}
          </span>
        </span>
        <ChevronUp size={16} className="flex-shrink-0 text-[#9A96C4]" strokeWidth={1.8} />
      </button>
    </div>
  );
}
